import { useCallback, useEffect, useState } from "react";

import { Icon, InputGroup } from "@blueprintjs/core";
import debounce from "lodash/debounce";

import CreditSVG from "@/../../hypetrain/static/img/credit.svg";
import { usePagination } from "@/components/helpers/custom-hooks/use-pagination";
import { EmbedSvg } from "@/components/helpers/ui/EmbedSvg";
import { Box, Flex } from "@/components/layout/flexbox";
import { PaginationButton } from "@/components/pieces/form/PaginationButton";
import { SimpleTable } from "@/components/pieces/table/SimpleTable";
import { SUBTEXT_COLOR } from "@/css/constants";
import {
  CoachWorkspacePricesQuery,
  ProductDetailQuery,
  useCoachWorkspacePricesLazyQuery,
} from "@/graphql";
import { ensureNonNullArray } from "@/utils/array";
import { formatCredits } from "@/utils/string";
import { NonNullableArrayItem } from "@/utils/types";

const PAGE_SIZE = 10;

type PriceOption = NonNullableArrayItem<
  NonNullable<CoachWorkspacePricesQuery["coachWorkspacePrices"]>
>;

const optionColumns = [
  {
    id: "workspace",
    header: <th align="left">Workspace</th>,
    renderCell: (option: PriceOption) => {
      return (
        <td align="left">
          <Box>{option?.workspace?.name}</Box>
        </td>
      );
    },
  },
  {
    id: "price-for-members",
    header: <th align="right">Price For Members</th>,
    renderCell: (option: PriceOption) => {
      const { coachCreditValue } = option || {};
      return (
        <td align="right">
          <Box>
            <Box display={"inline-flex"}>
              <EmbedSvg src={CreditSVG} height={16} width={16} />
              <Box ml={2} className="dot-line" css={{ display: "inline" }}>
                {formatCredits(option.price)}
              </Box>
            </Box>
            {coachCreditValue && (
              <Box color={SUBTEXT_COLOR} mt={"5px"}>
                <Icon
                  icon="lightning"
                  color={SUBTEXT_COLOR}
                  style={{ marginRight: 5 }}
                />
                You earn {formatCredits(coachCreditValue)}
              </Box>
            )}
          </Box>
        </td>
      );
    },
  },
];

interface WorkspaceSettingProps {
  coachProduct?: ProductDetailQuery["productById"]["coachProduct"];
}

export function WorkspaceSetting({ coachProduct }: WorkspaceSettingProps) {
  const productId = coachProduct?.productId;
  const [getCoachWorkspaceData, { data }] = useCoachWorkspacePricesLazyQuery();
  const options = ensureNonNullArray(data?.coachWorkspacePrices);

  useEffect(() => {
    if (productId) {
      getCoachWorkspaceData({
        variables: {
          productId,
        },
      });
    }
  }, [productId]);

  const [filteredOptions, setFilteredOptions] = useState<PriceOption[]>([]);

  const {
    startIndex,
    endIndex,
    back: backPage,
    next: nextPage,
    setCurrentPage,
  } = usePagination({
    pageSize: PAGE_SIZE,
    total: filteredOptions.length,
  });

  const onSearch = useCallback(
    debounce((text) => {
      setFilteredOptions(
        options.filter(({ workspace }) => workspace?.name.includes(text)),
      );
      setCurrentPage(1);
    }, 500),
    [options],
  );

  useEffect(() => {
    setFilteredOptions(options);
  }, [options]);

  return (
    <>
      <Box py={4}>
        <Flex
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Box mb={2}>
            <Box fontSize={20} fontWeight={600}>
              Workspace Settings
            </Box>
            <Box fontSize={16} color={SUBTEXT_COLOR}>
              Workspaces that don't have settings will use the price same as
              default.
            </Box>
          </Box>
          <Box>
            <InputGroup
              autoFocus={false}
              leftIcon="search"
              placeholder="Filter Workspaces..."
              fill={true}
              large={true}
              color="blue"
              onChange={(e) => {
                onSearch(e.target.value);
              }}
              style={{ borderRadius: 6 }}
            />
          </Box>
        </Flex>
        <SimpleTable
          columns={optionColumns}
          rows={filteredOptions.slice(startIndex, endIndex + 1)}
          emptyText="No settings available"
        />
        {filteredOptions.length > 0 && (
          <Flex justifyContent="center" alignItems="center" pt={12}>
            <PaginationButton
              icon="chevron-left"
              style={{ opacity: 0.3 }}
              onClick={backPage}
            />
            <Box mx={3}>
              {startIndex + 1} - {endIndex + 1} of {filteredOptions.length}
            </Box>
            <PaginationButton
              icon="chevron-right"
              style={{ opacity: 0.3 }}
              onClick={nextPage}
            />
          </Flex>
        )}
      </Box>
    </>
  );
}
